import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Vision360Snapshot, Vision360Data } from "@/types/vision360";

export function useVision360Query(patientId: string | undefined) {
  return useQuery({
    queryKey: ["vision-360", patientId],
    queryFn: async (): Promise<Vision360Data | null> => {
      if (!patientId) return null;

      const { data, error } = await supabase
        .from("patient_vision_360")
        .select("patient_id, snapshot, generated_at, confidence")
        .eq("patient_id", patientId)
        .maybeSingle();

      if (error) {
        console.error("[Vision360] Error fetching snapshot:", error);
        throw error;
      }

      if (!data) return null;

      return {
        patient_id: data.patient_id,
        snapshot: data.snapshot as unknown as Vision360Snapshot,
        generated_at: data.generated_at,
        confidence: data.confidence,
      };
    },
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

export function useVision360Generate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patientId: string): Promise<Vision360Snapshot> => {
      console.log("[Vision360] Generating snapshot for patient:", patientId);

      const { data, error } = await supabase.functions.invoke("generate-vision-360", {
        body: { patient_id: patientId },
      });

      if (error) {
        console.error("[Vision360] Error generating snapshot:", error);
        throw error;
      }

      return data.snapshot;
    },
    onSuccess: (_, patientId) => {
      // Invalidate the query to refetch the new data
      queryClient.invalidateQueries({ queryKey: ["vision-360", patientId] });
    },
  });
}

export function useVision360Regenerate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patientId: string): Promise<Vision360Snapshot> => {
      console.log("[Vision360] Force regenerating snapshot for patient:", patientId);

      const { data, error } = await supabase.functions.invoke("generate-vision-360", {
        body: { patient_id: patientId, force_regenerate: true },
      });

      if (error) {
        console.error("[Vision360] Error regenerating snapshot:", error);
        throw error;
      }

      return data.snapshot;
    },
    onSuccess: (_, patientId) => {
      queryClient.invalidateQueries({ queryKey: ["vision-360", patientId] });
    },
  });
}
